import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Loader2, Ban, Undo2 } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const RULE_LABEL = {
  probe: "Scanner probes",
  injection: "Injection attempts",
  bad_ua: "Attack tools / scrapers",
  flood: "Per-IP flood",
  burst: "Burst limit",
  method: "Disallowed methods",
  banned: "Requests from banned IPs",
};

export const AdminFirewall = () => {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [ip, setIp] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => { api.get("/admin/firewall").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const act = async (path, body, msg) => {
    setBusy(path);
    try { await api.post(`/admin/firewall/${path}`, body); toast({ title: msg }); load(); }
    catch (e) { toast({ title: "Action failed", description: e.response?.data?.detail || "Please try again." }); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-firewall">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-bold text-foreground flex items-center gap-2 flex-1">
          <ShieldCheck size={18} className="text-[#16a34a]" /> Firewall
          <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded-full ${d.enabled ? "bg-[#16a34a] text-white" : "bg-rose-100 text-rose-600"}`} data-testid="firewall-state">
            {d.enabled ? "ON" : "OFF"}
          </span>
        </h2>
        <span className="text-xs text-muted-foreground">{d.rpm}/min · burst {d.burst}/5s · ban after {d.strikesBeforeBan} strikes</span>
        <button onClick={() => act(`toggle?on=${d.enabled ? "false" : "true"}`, {}, d.enabled ? "Firewall turned off" : "Firewall turned on")}
          disabled={!!busy} data-testid="firewall-toggle-btn"
          className="text-xs font-bold border border-border hover:bg-secondary rounded-xl px-3 py-1.5 transition-colors">
          {d.enabled ? "Turn off" : "Turn on"}
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 mb-4">{d.last24h} requests turned away in the last 24 hours.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">By rule (24h)</p>
          {Object.keys(d.byRule || {}).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing blocked yet.</p>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(d.byRule).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                <div key={k} className="flex items-center gap-3 text-sm" data-testid={`firewall-rule-${k}`}>
                  <span className="flex-1 text-foreground truncate">{RULE_LABEL[k] || k}</span>
                  <span className="text-xs font-bold text-white bg-[#16a34a] rounded-full px-2 py-0.5">{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">Active bans ({d.activeBans?.length || 0})</p>
          {(d.activeBans || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nobody is banned right now.</p>
          ) : (
            <div className="space-y-1.5 max-h-40 overflow-auto pr-1">
              {d.activeBans.map((b) => (
                <div key={`${b.ip}-${b.createdAt}`} className="flex items-center gap-2 text-sm" data-testid={`firewall-ban-${b.ip}`}>
                  <span className="font-mono text-xs text-foreground flex-1 truncate">{b.ip}</span>
                  <span className="text-xs text-muted-foreground truncate max-w-[45%]" title={b.reason}>{b.reason}</span>
                  <button onClick={() => act("unblock", { ip: b.ip }, `Unblocked ${b.ip}`)} disabled={!!busy}
                    data-testid={`firewall-unblock-${b.ip}`} className="text-[#16a34a] hover:underline text-xs font-semibold inline-flex items-center gap-1">
                    <Undo2 size={12} /> Unblock
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="Block an IP (e.g. 203.0.113.9)"
          data-testid="firewall-ip-input"
          className="flex-1 min-w-[200px] bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#16a34a]/40" />
        <button onClick={() => act("block", { ip, minutes: 1440, reason: "blocked by admin" }, `Blocked ${ip} for 24h`)}
          disabled={!ip.trim() || !!busy} data-testid="firewall-block-btn"
          className="inline-flex items-center gap-1.5 text-sm font-bold bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white rounded-xl px-3 py-2 transition-colors">
          {busy === "block" ? <Loader2 className="animate-spin" size={14} /> : <Ban size={14} />} Block 24h
        </button>
      </div>

      {(d.recent || []).length > 0 && (
        <details className="mt-4">
          <summary className="text-xs font-bold text-muted-foreground cursor-pointer" data-testid="firewall-recent-toggle">Recent blocked requests</summary>
          <div className="mt-2 space-y-1 max-h-56 overflow-auto pr-1">
            {d.recent.map((e, i) => (
              <div key={i} className="text-xs flex items-center gap-2 border-b border-border/50 py-1 last:border-0">
                <span className="font-mono text-muted-foreground w-28 shrink-0 truncate">{e.ip}</span>
                <span className="font-semibold text-foreground w-20 shrink-0">{e.rule}</span>
                <span className="text-muted-foreground flex-1 min-w-0 truncate">{e.method} {e.path}{e.query ? `?${e.query}` : ""}</span>
                <span className="text-muted-foreground shrink-0">{String(e.ts).slice(5, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};
